import * as Radix from "@radix-ui/themes";
import type * as RemixReact from "@remix-run/react";
import { Components, Vivid } from "@vendrinc/frontend-ts";
import classnames from "classnames";
import * as Motion from "framer-motion";
import * as React from "react";
import * as UseHooks from "usehooks-ts";
import { AuthenticationDialog } from "./AuthenticationDialog";
import { Logo } from "./Header/Logo";
import styles from "./Header.module.css";
import { IntercomLauncherId } from "./intercom";
import * as Search from "~/components/Search";
import { Variant } from "~/components/Search";
import { AuthenticationContext } from "~/contexts/authenticationContext";
import { DialogContext } from "~/contexts/dialogContext";
import { MobileMenuContext } from "~/contexts/mobileMenuContext";
import { ReactRouterStubsContext } from "~/contexts/reactRouterStubsContext";
import { SearchContext } from "~/contexts/searchContext";
import { IdentityClaims, hasOptedIntoPlatform } from "~/domain/tokens";
import * as Analytics from "~/hooks/useAnalytics";
import { usePrefab } from "~/hooks/usePrefab";
import { useShowIntercom } from "~/hooks/useShowIntercom";
import * as Image from "~/image";
import * as paths from "~/paths";

const navMenus = [
  {
    label: "Buyers",
    menu: {
      title: "Vendr for Buyers",
      description:
        "Get a fair deal on SaaS with our proprietary $5B in contract data, expert insights, and negotiation support.",
      items: [
        {
          title: "Pricing Insights & Validation",
          description: "Discover what you should pay for software",
          href: paths.home(),
          icon: Image.pricingInsightsNavIcon,
        },
        {
          title: "Price Checks",
          description: "Know you're getting a fair deal",
          href: paths.priceChecks(),
          icon: Image.priceChecksNavIcon,
        },
        {
          title: "Vendr Pro",
          description:
            "Get personalized expert support, contract analysis and hands-on negotiation assistance",
          href: paths.intelligence(),
          icon: Image.vendrProNavIcon,
        },
      ],
    },
  },
];

export type HeaderProps = {
  platformAppUrl?: string;
  isMySuppliersEnabled?: boolean;
  channelTargetOrigin?: string;
  onMobileMenuClicked?: () => void;
};

export const Header: React.FC<HeaderProps> = ({
  platformAppUrl,
  isMySuppliersEnabled,
  channelTargetOrigin,
  onMobileMenuClicked,
}) => {
  const stubs = React.useContext(ReactRouterStubsContext);

  const { identity } = React.useContext(AuthenticationContext);
  const [showMobileSearch, setShowMobileSearch] = React.useState(false);
  const [hideSearch, setHideSearch] = React.useState(true);
  const { pathname } = stubs.location;
  const error = stubs.routeError;

  const isMarketplace = pathname === paths.marketplace();
  const isHome = pathname === paths.home();
  const isSellers = pathname === paths.vendrVerified();
  const isRequests = pathname.startsWith(paths.myRequests());

  // FIXME We should be using Radix themes, but no time
  const theme = isSellers ? "dark" : "light";
  const isDarkTheme = theme === "dark";

  // Search bar state handling
  React.useEffect(() => {
    setHideSearch(isMarketplace || isHome || isSellers || Boolean(error));
  }, [pathname, error, isMarketplace, isHome, isSellers]);

  const { searchInputRef } = React.useContext(SearchContext);
  const observer = React.useRef<IntersectionObserver | null>(null);
  React.useEffect(() => {
    observer.current = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) =>
          entry.isIntersecting ? setHideSearch(true) : setHideSearch(false),
        );
      },
    );

    if (searchInputRef?.current !== null) {
      // If the ref is not null we know we are on marketplace or home
      observer.current?.observe(searchInputRef!.current);
    }
    return () => {
      observer.current?.disconnect();
    };
  }, [searchInputRef, pathname]);

  // Mobile menu state handling
  const { isMobileMenuOpen, setIsMobileMenuOpen } =
    React.useContext(MobileMenuContext);
  React.useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname, setIsMobileMenuOpen]);

  const { state } = stubs.navigation;
  React.useEffect(() => {
    if (state === "loading") {
      setIsMobileMenuOpen(false);
    }
  }, [state, setIsMobileMenuOpen]);

  // Auth menu state handling
  const [searchParams] = stubs.searchParams;
  const [authDialogIsOpen, setAuthDialogIsOpen] = React.useState(() =>
    searchParams.has("login"),
  );

  // Close mobile menu on resize
  const windowSize = UseHooks.useWindowSize({ initializeWithValue: false });
  React.useEffect(() => {
    if (windowSize.width && windowSize.width > 1024) {
      setIsMobileMenuOpen(false);
    }
  }, [windowSize, setIsMobileMenuOpen]);

  const [scrollY, setScrollY] = React.useState(0);
  UseHooks.useEventListener("scroll", () => {
    setScrollY(window.scrollY);
  });

  const leftSide = (() => {
    return <LandingPageNavMenus theme={theme} />;
  })();

  return (
    <Motion.LazyMotion strict features={Motion.domAnimation}>
      <Motion.m.div
        id="main-header"
        animate={isDarkTheme ? { opacity: 1 } : undefined}
        className={classnames(
          styles.header,
          isMobileMenuOpen ? styles.mobileMenuOpen : null,
        )}
        initial={isDarkTheme ? { opacity: 0 } : undefined}
        style={{
          backgroundColor: isHome ? "rgba(255,255,255,0.75)" : "transparent",
          backdropFilter: `blur(20px) opacity(${isRequests ? 1 : Motion.easeInOut(scrollY / 200)})`,
          position: "sticky",
          top: "0px",
          width: "100%",
        }}
        transition={isDarkTheme ? { ease: "easeIn", duration: 0.8 } : undefined}
      >
        {/* md+ */}
        <Radix.Flex
          align="center"
          justify="between"
          height="100%"
          pl="5"
          pr="5"
          gap="4"
          display={{
            initial: "none",
            md: "flex",
          }}
        >
          <Radix.Flex align="center" gap="3">
            <VendrLogo theme={theme} />
            {leftSide}
          </Radix.Flex>
          <Radix.Flex flexGrow="1" justify="end">
            <Radix.Box
              width={{
                initial: "300px",
                md: "400px",
              }}
            >
              {!(isMobileMenuOpen || hideSearch) && (
                <Radix.Flex>
                  <Search.Input
                    placeholder={"Search software products"}
                    variant={Variant.header2}
                  />
                </Radix.Flex>
              )}
            </Radix.Box>
          </Radix.Flex>

          {/* flag check to display or not to display is done in the component */}
          {identity && (
            <Radix.Flex
              display={{
                initial: "none",
                sm: "none",
              }}
            >
              <Link
                label={"My Requests"}
                href={"/requests"}
                reloadDocument={false}
                theme={theme}
              />
            </Radix.Flex>
          )}
          {/* Right side menu: we cannot guarantee we have that value for error pages, so we need to hide this menu. */}
          {platformAppUrl && (
            <Radix.Flex gridColumn="3" justify="end" gap="2">
              <Radix.Box className={styles.desktopAuthBox}>
                <AuthBox
                  setAuthDialogIsOpen={setAuthDialogIsOpen}
                  authDialogIsOpen={authDialogIsOpen}
                  platformAppUrl={platformAppUrl}
                  isMySuppliersEnabled={isMySuppliersEnabled}
                  channelTargetOrigin={channelTargetOrigin}
                />
              </Radix.Box>
            </Radix.Flex>
          )}
        </Radix.Flex>

        {/* mobile */}
        {!showMobileSearch && (
          <Radix.Flex
            align="center"
            justify="between"
            height="100%"
            pl="4"
            pr="4"
            gap="4"
            display={{
              initial: "flex",
              md: "none",
            }}
          >
            <Radix.Flex align="center" gap="3">
              <Radix.Box className={styles.mobileMenuToggle}>
                <MobileMenuToggle
                  isMobileMenuOpen={isMobileMenuOpen}
                  onMobileMenuClick={() => {
                    setIsMobileMenuOpen(!isMobileMenuOpen);

                    if (onMobileMenuClicked) {
                      onMobileMenuClicked();
                    }
                  }}
                  theme={theme}
                />
              </Radix.Box>
            </Radix.Flex>
            <Radix.Flex align="center" gap="3">
              <VendrLogo theme={theme} />
            </Radix.Flex>
            <Radix.Flex>
              <button
                type="button"
                aria-label="Toggle search"
                className={classnames(
                  styles.button,
                  styles.searchButton,
                  theme === "dark" ? styles.dark : null,
                )}
                onClick={(e) => {
                  e.preventDefault();
                  setShowMobileSearch(!showMobileSearch);
                }}
              >
                <div className={styles.buttonIcon}>{Vivid.Icon.search}</div>
              </button>
            </Radix.Flex>
          </Radix.Flex>
        )}
        {showMobileSearch && (
          <Radix.Flex
            align="center"
            justify="between"
            height="100%"
            pl="4"
            pr="4"
            gap="4"
            display={{
              initial: "flex",
              md: "none",
            }}
          >
            <Radix.Flex>
              <button
                aria-label="Hide mobile search"
                className={classnames(
                  styles.button,
                  styles.backButton,
                  theme === "dark" ? styles.dark : null,
                )}
                onClick={(e) => {
                  e.preventDefault();
                  setShowMobileSearch(false);
                }}
              >
                <div className={styles.buttonIcon}>{Vivid.Icon.arrowLeft}</div>
              </button>
            </Radix.Flex>
            <Radix.Flex flexGrow="1" justify="end">
              <Search.Input
                placeholder={"Search software products"}
                variant={Variant.header2}
              />
            </Radix.Flex>
          </Radix.Flex>
        )}
      </Motion.m.div>
      <Motion.LazyMotion strict features={Motion.domAnimation}>
        <Motion.AnimatePresence>
          {isMobileMenuOpen && (
            <Motion.m.div
              className={styles.mobileMenu}
              key="mobile-menu"
              initial={{ opacity: 0, translateY: -4 }}
              animate={{ opacity: 1, translateY: 0 }}
              exit={{ opacity: 0, translateY: -4 }}
              transition={{ duration: 0.2 }}
            >
              <MobileMenu
                toggleMobileMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                // Auth dialog state
                authDialogIsOpen={authDialogIsOpen}
                setAuthDialogIsOpen={setAuthDialogIsOpen}
                platformAppUrl={platformAppUrl}
              />
            </Motion.m.div>
          )}
        </Motion.AnimatePresence>
      </Motion.LazyMotion>
    </Motion.LazyMotion>
  );
};

export const VendrLogo: React.FC<{ theme: "light" | "dark" }> = ({ theme }) => {
  const { trackLink } = Analytics.useAnalytics();
  const stubs = React.useContext(ReactRouterStubsContext);

  return (
    <stubs.Link
      to={paths.home()}
      prefetch="intent"
      className={styles.logo}
      onClick={() =>
        trackLink({
          element_text: "Vendr icon",
          element_location: "Site navigation",
        })
      }
    >
      <Logo theme={theme} />
    </stubs.Link>
  );
};

const LandingPageNavMenus: React.FC<{ theme: "light" | "dark" }> = ({
  theme,
}) => {
  const prefab = usePrefab();
  const isChromeExtensionEnabled = prefab.isEnabled(
    "chrome-extension-header-link",
  );

  return (
    <Radix.Flex gap="3" className={styles.desktopLinks}>
      {navMenus.map((link) => {
        return (
          <LandingPageMenu key={link.label} theme={theme} label={link.label}>
            <LandingPageMenuHeader
              title={link.menu.title}
              description={link.menu.description}
            />

            <Components.PopoverMenuSeparator />

            {link.menu.items.map((i) => (
              <LandingPageMenuLink
                key={i.href}
                href={i.href}
                title={i.title}
                description={i.description}
                icon={i.icon}
              />
            ))}
          </LandingPageMenu>
        );
      })}

      <Link
        key={paths.vendrVerified()}
        label={"Sellers"}
        href={paths.vendrVerified()}
        reloadDocument={false}
        theme={theme}
      />
      {isChromeExtensionEnabled && (
        <Link
          key={paths.chromeExtensionMarketingPage()}
          label={"Chrome Extension"}
          href={paths.chromeExtensionMarketingPage()}
          reloadDocument={true}
          theme={theme}
        />
      )}
      <Link
        key={paths.about()}
        label={"About"}
        href={paths.about()}
        reloadDocument={true}
        theme={theme}
      />
    </Radix.Flex>
  );
};

const LandingPageMenuHeader: React.FC<{
  title: string;
  description: string;
}> = (props) => {
  return (
    <Radix.Flex direction="column" gap="6px" p="12px">
      <Radix.Box>
        <Vivid.Text weight="bold" size="14px" color="nextGreyscale900">
          {props.title}
        </Vivid.Text>
      </Radix.Box>

      <Radix.Box>
        <Vivid.Text size="14px" color="nextGreyscale600">
          {props.description}
        </Vivid.Text>
      </Radix.Box>
    </Radix.Flex>
  );
};

const LandingPageMenuLink: React.FC<{
  href: string;
  icon: string;
  title: string;
  description: string;
}> = (props) => {
  const stubs = React.useContext(ReactRouterStubsContext);
  const { pathname } = stubs.location;

  return props.href === pathname ? (
    <Components.PopoverMenuItem active>
      <Radix.Flex gap="20px" align="center">
        <Radix.Flex
          align="center"
          justify="center"
          className={styles.popoverMenuItemIcon}
        >
          <img src={props.icon} alt={`Icon for ${props.title}`} />
        </Radix.Flex>
        <Radix.Flex direction="column" gap="6px">
          <Radix.Box>
            <Vivid.Text weight="bold" size="14px" color="nextGreyscale900">
              {props.title}
            </Vivid.Text>
          </Radix.Box>

          <Radix.Box>
            <Vivid.Text size="14px" color="nextGreyscale600">
              {props.description}
            </Vivid.Text>
          </Radix.Box>
        </Radix.Flex>
      </Radix.Flex>
    </Components.PopoverMenuItem>
  ) : (
    <Components.PopoverMenuItem>
      <a href={props.href} className={styles.popoverMenuLink}>
        <Radix.Flex gap="20px" align="center">
          <Radix.Flex
            align="center"
            justify="center"
            className={styles.popoverMenuItemIcon}
          >
            <img src={props.icon} alt={`Icon for ${props.title}`} />
          </Radix.Flex>
          <Radix.Flex direction="column" gap="6px">
            <Radix.Box>
              <Vivid.Text weight="bold" size="14px" color="nextGreyscale900">
                {props.title}
              </Vivid.Text>
            </Radix.Box>

            <Radix.Box>
              <Vivid.Text size="14px" color="nextGreyscale600">
                {props.description}
              </Vivid.Text>
            </Radix.Box>
          </Radix.Flex>
        </Radix.Flex>
      </a>
    </Components.PopoverMenuItem>
  );
};

const LandingPageMenu: React.FC<{
  children?: React.ReactNode;
  theme: "light" | "dark";
  label: string;
}> = (props) => {
  const dialogContext = React.useContext(DialogContext);

  const { trackButton } = Analytics.useAnalytics();
  const [isOpen, setIsOpen] = React.useState(false);

  const className = classnames(
    styles.link,
    styles.button,
    props.theme === "dark" ? styles.dark : null,
  );

  const icon = isOpen ? Vivid.Icon.chevronUp : Vivid.Icon.chevronDown;

  const onClick = () => {
    trackButton({
      element_text: props.label,
      element_location: "Site menu navigation",
    });
  };

  return (
    <Components.PopoverMenu
      align="start"
      container={dialogContext.container.current}
      trigger={
        <button type="button" className={className} onClick={onClick}>
          <Radix.Flex align="center" px="4" height="40px" gap="2">
            <div>{props.label}</div>
            <div className={styles.buttonIcon}>{icon}</div>
          </Radix.Flex>
        </button>
      }
      onOpenChange={setIsOpen}
    >
      <Radix.Flex direction="column" maxWidth="400px">
        {props.children}
      </Radix.Flex>
    </Components.PopoverMenu>
  );
};

const Link: React.FC<{
  label: string;
  href: string;
  theme: "light" | "dark";
  type?: "vendrMarketplace" | "marketplace";
  reloadDocument: boolean;
  desktopOnly?: boolean;
}> = (props) => {
  const stubs = React.useContext(ReactRouterStubsContext);
  const { trackLink } = Analytics.useAnalytics();

  const className = classnames(
    styles.link,
    props.type === "marketplace" ? styles.marketplace : null,
    props.theme === "dark" ? styles.dark : null,
    props.desktopOnly ? styles.desktopLinks : null,
  );

  return (
    <stubs.Link
      onClick={() =>
        trackLink({
          element_text: props.label,
          element_location: "Site desktop navigation",
        })
      }
      to={props.href}
      prefetch="intent"
      className={className}
      reloadDocument={props.reloadDocument}
    >
      <Radix.Flex align="center" px="4" height="40px">
        {props.label}
      </Radix.Flex>
    </stubs.Link>
  );
};

export const Authenticated: React.FC<{
  identity: IdentityClaims;
  platformOptedInCookie: string | null;
  platformAppUrl: string;
  location: RemixReact.Location;
  isMySuppliersEnabled?: boolean;
}> = ({
  identity,
  platformAppUrl,
  location,
  platformOptedInCookie,
  isMySuppliersEnabled,
}) => {
  const stubs = React.useContext(ReactRouterStubsContext);
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const { trackButton, trackLink } = Analytics.useAnalytics();
  const showIntercom = useShowIntercom();
  const prefab = usePrefab();

  const dialogContext = React.useContext(DialogContext);

  return (
    <Components.ProfileMenu
      onOpenChange={(isOpen) => {
        trackButton({
          element_text: `Toggle user menu ${isOpen ? "open" : "closed"}`,
          element_location: "Site desktop navigation",
        });
      }}
      name={identity.name}
      avatarUrl={identity.profileImage ?? undefined}
      container={dialogContext.container.current}
    >
      <Components.ProfileMenuItem>
        <stubs.Link
          className={styles.profileMenuText}
          to="/my-orders"
          onClick={() =>
            trackButton({
              element_text: "My orders",
              element_location: "Site desktop navigation",
            })
          }
          prefetch="intent"
        >
          My orders
        </stubs.Link>
      </Components.ProfileMenuItem>

      <Radix.Flex
        display={{
          initial: "flex",
          sm: "flex",
        }}
      >
        <Components.ProfileMenuItem>
          <stubs.Link
            className={styles.profileMenuText}
            to="/requests"
            onClick={() =>
              trackButton({
                element_text: "My requests",
                element_location: "Site desktop navigation",
              })
            }
            prefetch="intent"
          >
            My requests
          </stubs.Link>
        </Components.ProfileMenuItem>
      </Radix.Flex>

      {isMySuppliersEnabled && (
        <Components.ProfileMenuItem>
          <stubs.Link
            className={styles.profileMenuText}
            to={paths.buyers.index()}
            onClick={() =>
              trackButton({
                element_text: "My suppliers",
                element_location: "Site desktop navigation",
              })
            }
            prefetch="intent"
          >
            My suppliers
          </stubs.Link>
        </Components.ProfileMenuItem>
      )}

      {prefab.isEnabled("batch-uploads") && (
        <Components.ProfileMenuItem>
          <stubs.Link
            className={styles.profileMenuText}
            to={paths.buyers.index()}
            onClick={() =>
              trackButton({
                element_text: "Batch uploads",
                element_location: "Site desktop navigation",
              })
            }
            prefetch="intent"
          >
            Buyer Portal
          </stubs.Link>
        </Components.ProfileMenuItem>
      )}

      <Components.ProfileMenuItem>
        <stubs.Link
          className={styles.profileMenuText}
          to="#"
          onClick={() => {
            showIntercom();
            trackButton({
              element_text: "Help",
              element_location: "Site desktop navigation",
            });
          }}
          id={IntercomLauncherId}
        >
          Help
        </stubs.Link>
      </Components.ProfileMenuItem>

      <Components.ProfileMenuSeparator />

      {hasOptedIntoPlatform(identity, platformOptedInCookie) && (
        <Components.ProfileMenuItem>
          <stubs.Link
            className={styles.profileMenuText}
            to={platformAppUrl}
            onClick={() =>
              trackLink({
                element_text: "Go to platform",
                element_location: "Site desktop navigation",
              })
            }
          >
            Go to Platform
          </stubs.Link>
        </Components.ProfileMenuItem>
      )}

      <stubs.Form
        method="post"
        action={paths.logout({ redirectBackTo: location })}
        onSubmit={() => setIsLoggingOut(true)}
      >
        <Components.ProfileMenuItem>
          <button
            className={classnames(styles.logoutButton, styles.profileMenuText)}
            onClick={() => {
              trackButton({
                element_text: "Log out",
                element_location: "Site desktop navigation",
              });
            }}
            type="submit"
            disabled={isLoggingOut}
          >
            {isLoggingOut ? "Logging out..." : "Log out"}
          </button>
        </Components.ProfileMenuItem>
      </stubs.Form>
    </Components.ProfileMenu>
  );
};

const AuthHeader: React.FC<{
  platformAppUrl: string;
  setDialogIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isMySuppliersEnabled?: boolean;
}> = ({ platformAppUrl, setDialogIsOpen, isMySuppliersEnabled }) => {
  const { identity, platformOptedInCookie } = React.useContext(
    AuthenticationContext,
  );
  const stubs = React.useContext(ReactRouterStubsContext);
  const { location } = stubs;
  const { trackButton } = Analytics.useAnalytics();

  return identity ? (
    <Authenticated
      identity={identity}
      platformOptedInCookie={platformOptedInCookie}
      location={location}
      platformAppUrl={platformAppUrl}
      isMySuppliersEnabled={isMySuppliersEnabled}
    />
  ) : (
    <div>
      <button
        type="button"
        className={classnames(styles.loginButton)}
        onClick={() => {
          trackButton({
            element_text: "Login",
            element_location: "Site desktop navigation",
          });

          setDialogIsOpen(true);
        }}
      >
        <div className={styles.buttonIcon}>{Vivid.Icon.profile}</div>
        Login
      </button>
    </div>
  );
};

const AuthBox: React.FC<{
  platformAppUrl: string;
  authDialogIsOpen: boolean;
  isMySuppliersEnabled?: boolean;
  setAuthDialogIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  channelTargetOrigin?: string;
}> = ({ setAuthDialogIsOpen, isMySuppliersEnabled, ...props }) => {
  const stubs = React.useContext(ReactRouterStubsContext);
  const [searchParams, setSearchParams] = stubs.searchParams;

  const setDialogOpenClosed = React.useCallback(
    (isOpen: boolean) => {
      if (searchParams.has("login")) {
        setSearchParams((currentSearchParams) => {
          currentSearchParams.delete("login");
          return currentSearchParams;
        });
      }

      setAuthDialogIsOpen(isOpen);
    },
    [searchParams, setSearchParams, setAuthDialogIsOpen],
  );

  const redirectTo =
    searchParams.has("login") && searchParams.has("redirect")
      ? searchParams.get("redirect")
      : null;

  return (
    <>
      <AuthHeader
        platformAppUrl={props.platformAppUrl}
        setDialogIsOpen={setAuthDialogIsOpen}
        isMySuppliersEnabled={isMySuppliersEnabled}
      />
      <AuthenticationDialog
        platformAppUrl={props.platformAppUrl}
        redirectTo={redirectTo}
        dialogIsOpen={props.authDialogIsOpen}
        setDialogIsOpen={setDialogOpenClosed}
        channelTargetOrigin={props.channelTargetOrigin}
      />
    </>
  );
};

const MobileMenuToggle: React.FC<{
  isMobileMenuOpen: boolean;
  onMobileMenuClick: () => void;
  theme: "light" | "dark";
}> = (props) => {
  const { trackButton } = Analytics.useAnalytics();
  return (
    <button
      aria-label="Toggle mobile menu"
      className={classnames(
        styles.mobileMenuToggleLines,
        props.isMobileMenuOpen && styles.mobileMenuToggleOpen,
        props.theme === "dark" ? styles.dark : null,
      )}
      onClick={() => {
        if (props.isMobileMenuOpen) {
          trackButton({
            element_text: "Hide mobile menu",
            element_location: "Site mobile navigation",
          });
        } else {
          trackButton({
            element_text: "Show mobile menu",
            element_location: "Site mobile navigation",
          });
        }

        props.onMobileMenuClick();
      }}
    >
      <Radix.Box as="span"></Radix.Box>
      <Radix.Box as="span"></Radix.Box>
      <Radix.Box as="span"></Radix.Box>
      <Radix.Box as="span"></Radix.Box>
    </button>
  );
};

const MobileLink: React.FC<{
  href: string;
  id?: string;
  children: React.ReactNode;
  onClick: () => void;
  reloadDocument: boolean;
  standalone?: boolean;
}> = (props) => {
  const stubs = React.useContext(ReactRouterStubsContext);

  return (
    <stubs.Link
      to={props.href}
      id={props.id}
      className={classnames(
        styles.mobileLink,
        "animatedMenuItem",
        props.standalone ? styles.standalone : undefined,
      )}
      onClick={props.onClick}
      reloadDocument={props.reloadDocument}
    >
      <Radix.Box px="5">
        <Vivid.Text
          size={props.standalone ? "16px" : "14px"}
          weight={props.standalone ? "bold" : undefined}
        >
          {props.children}
        </Vivid.Text>
      </Radix.Box>
    </stubs.Link>
  );
};

const MobileNavMenu: React.FC<{
  label: string;
  index: number;
  items: { href: string; title: string; onClick?: () => void }[];
}> = (props) => {
  const { trackLink } = Analytics.useAnalytics();

  return (
    <React.Fragment key={props.label}>
      {props.index !== 0 && <Radix.Separator color="gray" size="4" />}

      <Radix.Flex direction="column" gap="3">
        <Radix.Box px="5" py="2">
          <Vivid.Text weight="bold" size="20px">
            {props.label}
          </Vivid.Text>
        </Radix.Box>

        <Radix.Separator color="gray" size="4" />

        {props.items.map((item, itemIndex) => {
          return (
            <React.Fragment key={`${props.label}-${itemIndex}`}>
              {itemIndex !== 0 && <Radix.Separator color="gray" size="4" />}

              <MobileLink
                href={item.href}
                reloadDocument={true}
                onClick={() => {
                  trackLink({
                    element_text: item.title,
                    element_location: "Site mobile navigation",
                  });
                  if (item.onClick) {
                    item.onClick();
                  }
                }}
              >
                {item.title}
              </MobileLink>
            </React.Fragment>
          );
        })}
      </Radix.Flex>
    </React.Fragment>
  );
};

const MobileMenu: React.FC<{
  toggleMobileMenu: () => void;
  authDialogIsOpen: boolean;
  setAuthDialogIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  platformAppUrl?: string;
}> = (props) => {
  const stubs = React.useContext(ReactRouterStubsContext);

  const { identity, platformOptedInCookie } = React.useContext(
    AuthenticationContext,
  );

  const { trackLink, trackButton } = Analytics.useAnalytics();

  const [scope, animate] = Motion.useAnimate();

  React.useEffect(() => {
    void animate(
      ".animatedMenuItem",
      { opacity: 1, transform: "translateX(0)" },
      { delay: Motion.stagger(0.065, { startDelay: 0.2, ease: "easeIn" }) },
    );
  }, [animate]);

  UseHooks.useScrollLock();

  const showIntercom = useShowIntercom();

  const navMenuLinks = navMenus.map((nv, nvIndex) => {
    return (
      <MobileNavMenu
        key={nv.label}
        index={nvIndex}
        label={nv.label}
        items={nv.menu.items}
      />
    );
  });

  const login = (
    <button
      type="button"
      className={classnames(styles.loginButton, styles.loginButtonMobile)}
      onClick={() => {
        trackButton({
          element_text: "Login",
          element_location: "Site desktop navigation",
        });
        props.setAuthDialogIsOpen(true);
        props.toggleMobileMenu();
      }}
    >
      <div className={styles.buttonIcon}>{Vivid.Icon.profile}</div>
      Login
    </button>
  );

  const logout = (
    <stubs.Form
      method="post"
      action={paths.logout({
        redirectBackTo: {
          key: "",
          hash: location.hash,
          state: {},
          search: location.search,
          pathname: location.pathname,
        },
      })}
    >
      <button
        type="submit"
        className={classnames(styles.loginButton, styles.loginButtonMobile)}
        onClick={() => {
          trackButton({
            element_text: "Log out",
            element_location: "Site desktop navigation",
          });
          props.setAuthDialogIsOpen(true);
        }}
      >
        Log out
      </button>
    </stubs.Form>
  );

  return (
    <Radix.Flex ref={scope} direction={"column"} gap={"3"} pt={"5"}>
      {identity && (
        <React.Fragment>
          <MobileNavMenu
            index={0}
            label="Your Account"
            items={(() => {
              const items = [
                {
                  title: "My Requests",
                  href: paths.myRequests(),
                },
                {
                  title: "Help",
                  href: "#",
                  onClick: () => {
                    showIntercom();
                  },
                },
              ];

              if (
                props.platformAppUrl &&
                hasOptedIntoPlatform(identity, platformOptedInCookie)
              ) {
                items.push({
                  title: "Go to Platform",
                  href: props.platformAppUrl,
                });
              }

              return items;
            })()}
          />

          <Radix.Separator color="gray" size="4" />
        </React.Fragment>
      )}

      {navMenuLinks}

      <Radix.Separator color="gray" size="4" />

      <Radix.Flex py="2">
        <MobileLink
          href={paths.vendrVerified()}
          reloadDocument={false}
          onClick={() =>
            trackLink({
              element_text: "Sellers",
              element_location: "Site mobile navigation",
            })
          }
          standalone
        >
          Sellers
        </MobileLink>
      </Radix.Flex>

      <Radix.Separator color="gray" size="4" />

      <Radix.Flex pt="2">
        <MobileLink
          href={paths.about()}
          reloadDocument={true}
          onClick={() =>
            trackLink({
              element_text: "About",
              element_location: "Site mobile navigation",
            })
          }
          standalone
        >
          About
        </MobileLink>
      </Radix.Flex>

      {props.platformAppUrl && (
        <Radix.Box px="5" pt="2">
          {identity ? logout : login}
        </Radix.Box>
      )}
    </Radix.Flex>
  );
};
